/**
 * A titled sparkline with a current-value header and a legend.
 *
 * One of these per monitor - CPU, memory, each GPU, temperatures, network, disk usage - which is the
 * shape the XAML built out of a `Grid`, a `Viewbox`, two nested `ItemsControl`s and a `Polyline`
 * template. Here the chart is a control that paints itself, so this is only the furniture around it.
 *
 * The legend is what makes a multi-series chart readable, and it carries the CURRENT value per
 * series rather than just a name: on a temperature chart with five sensors, which line is which
 * matters far less than which sensor is at 91 °C.
 */
import { insets, type Color, type Control, type RenderContext, type Theme } from 'webwin';
import { Sparkline, chartColor, type Series } from './Sparkline.js';
import { Card } from './Card.js';
import { CrmLabel } from './Text.js';
import { buttonRow, fillWith, FONT, GAP, hstack, splitRow, stackOf, value, vstack } from './layout.js';

export interface ChartSpec {
  readonly title: string;
  /** The right-hand header: the latest reading, or the axis when the axis is the interesting part. */
  readonly current: string;
  readonly series: readonly Series[];
  /** Hidden when empty - a single-series chart whose title already names it needs no legend. */
  readonly legend?: readonly { readonly text: string; readonly colorIndex: number }[];
  /** The ceiling. Null autoscales to the window's peak, which `current` should then name. */
  readonly max?: number | null;
  /** How one reading reads in the hover tooltip. Defaults to one decimal place, no unit. */
  readonly format?: (value: number) => string;
}

export class ChartCard {
  readonly panel: Control;

  readonly #title: CrmLabel;
  readonly #current: CrmLabel;
  readonly #chart: Sparkline;
  readonly #legend = vstack({ spacing: 0, hAlign: 'stretch', autoWidth: false });

  constructor(spec: ChartSpec) {
    this.#title = value('', { font: FONT.bold });
    // Measured, never wrapped: a squeezed wrapping label is cut to its first line ("3.7 GB of 9.6
    // GB ..."), and this is the number the card exists to show.
    this.#current = new CrmLabel({ text: '', font: FONT.small, role: 'muted', wrap: false, autoWidth: true, autoHeight: true });
    this.#chart = new Sparkline({ height: 48 });

    const header = splitRow({ align: 'center' });
    fillWith(header, this.#title);
    header.controls.add(this.#current);

    this.panel = stackOf(
      vstack({ spacing: GAP.xs, hAlign: 'stretch', autoWidth: false }),
      header,
      this.#chart,
      this.#legend
    );

    this.apply(spec);
  }

  apply(spec: ChartSpec): void {
    this.#title.text = spec.title;
    this.#current.text = spec.current;
    this.#chart.max = spec.max ?? null;
    if (spec.format) this.#chart.format = spec.format;
    this.#chart.series = spec.series;

    const legend = spec.legend ?? [];
    this.#legend.controls.clear();
    if (legend.length > 0) {
      // A WRAPPING row of unwrapped entries. The old row did the opposite - entries side by side in a
      // row that never wrapped, each label wrapping inside whatever width was left - so every label
      // was squeezed to a sliver and cut off after its first line.
      const row = buttonRow({ spacing: GAP.md, lineSpacing: GAP.xs, align: 'center' });
      for (const entry of legend) row.controls.add(legendChip(entry.text, entry.colorIndex));
      this.#legend.controls.add(row);
    }
    this.#legend.visible = legend.length > 0;
  }
}

/** A square of the series' own colour beside its label, so the pair can never disagree. */
function legendChip(text: string, colorIndex: number): Control {
  const swatch = new Swatch(colorIndex);
  const label = new CrmLabel({ text, font: FONT.small, role: 'muted', wrap: false, autoWidth: true, autoHeight: true });
  return stackOf(hstack({ spacing: GAP.xs, autoWidth: true, align: 'center' }), swatch, label);
}

/** An 8px square in a chart-palette colour, resolved in `onPaint` so a theme switch moves it. */
class Swatch extends Card {
  readonly #index: number;

  constructor(index: number) {
    super({ width: 8, height: 8, radius: 2, bordered: false, padding: insets(0) });
    this.#index = index;
  }

  protected override onPaint(rc: RenderContext, theme: Theme): void {
    const color: Color = chartColor(theme, this.#index);
    rc.fillRect(this.localRect, color, 2);
  }
}
